import React, { useState, useEffect } from "react"
import { lumi } from "../lib/lumi"
import {ArrowLeft, Copy, Check, Trash2, RotateCcw, RotateCw} from 'lucide-react'
import toast from "react-hot-toast"
import { useNavigate, useParams } from "react-router-dom"

interface Photo {
  _id: string
  fileName: string
  fileUrl: string
  alt: string
  category?: string
  tags?: string[]
  width?: number
  height?: number
  fileSize?: number
  uploadedBy: string
  displayLocations?: string[]  // 新增：顯示位置
  createdAt: string
  updatedAt: string
}

const PhotoEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [photo, setPhoto] = useState<Photo | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [copiedUrl, setCopiedUrl] = useState(false)
  const [rotation, setRotation] = useState(0)

  // 表單狀態
  const [editForm, setEditForm] = useState({
    alt: "",
    category: "general",
    tags: "",
    displayLocations: [] as string[]
  })

  useEffect(() => {
    if (id) {
      fetchPhoto(id)
    }
  }, [id])

  const fetchPhoto = async (photoId: string) => {
    setLoading(true)
    try {
      const photoData = await lumi.entities.photos.get(photoId)
      if (photoData) {
        setPhoto(photoData as Photo)
        setEditForm({
          alt: photoData.alt || "",
          category: photoData.category || "general",
          tags: photoData.tags?.join(", ") || "",
          displayLocations: photoData.displayLocations || []
        })
        setRotation(0)
      } else {
        toast.error("照片不存在")
        navigate("/admin/photos")
      }
    } catch (error) {
      console.error("Failed to fetch photo:", error)
      toast.error("載入照片失敗")
      navigate("/admin/photos")
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!photo) return

    setSaving(true)
    try {
      await lumi.entities.photos.update(photo._id, {
        alt: editForm.alt,
        category: editForm.category,
        tags: editForm.tags.split(",").map(tag => tag.trim()).filter(tag => tag),
        displayLocations: editForm.displayLocations,
        updatedAt: new Date().toISOString()
      })
      toast.success("照片資訊已更新")
      navigate("/admin/photos")
    } catch (error) {
      console.error("Update failed:", error)
      toast.error("更新失敗")
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!photo) return
    if (!confirm("確定要刪除這張照片嗎？")) return

    try {
      await lumi.tools.file.delete([photo.fileUrl])
      await lumi.entities.photos.delete(photo._id)
      toast.success("照片已刪除")
      navigate("/admin/photos")
    } catch (error) {
      console.error("Delete failed:", error)
      toast.error("刪除失敗")
    }
  }

  const handleCopyUrl = () => {
    if (!photo) return
    navigator.clipboard.writeText(photo.fileUrl)
    setCopiedUrl(true)
    toast.success("URL 已複製")
    setTimeout(() => setCopiedUrl(false), 2000)
  }

  const handleRotateLeft = () => {
    setRotation((prev) => (prev - 90) % 360)
  }

  const handleRotateRight = () => {
    setRotation((prev) => (prev + 90) % 360)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-xl text-gray-400">載入中...</div>
        </div>
      </div>
    )
  }

  if (!photo) {
    return null
  }

  return (
    <div className="min-h-screen bg-black text-gray-100">
      <div className="max-w-5xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/admin/photos")}
              className="p-2 hover:bg-gray-900 transition-colors"
              title="返回照片管理"
            >
              <ArrowLeft size={24} />
            </button>
            <div>
              <h1 className="text-3xl font-light tracking-wider mb-2">編輯照片</h1>
              <p className="text-gray-400">{photo.fileName}</p>
            </div>
          </div>

          <button
            onClick={handleDelete}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white transition-colors"
          >
            <Trash2 size={18} />
            刪除照片
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* 左側：圖片預覽 */}
          <div className="bg-gray-950 border border-gray-800 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-light tracking-wide">圖片預覽</h2>
              <div className="flex gap-2">
                <button
                  onClick={handleRotateLeft}
                  className="p-2 bg-gray-800 hover:bg-gray-700 transition-colors"
                  title="向左旋轉 90°"
                >
                  <RotateCcw size={20} />
                </button>
                <button
                  onClick={handleRotateRight}
                  className="p-2 bg-gray-800 hover:bg-gray-700 transition-colors"
                  title="向右旋轉 90°"
                >
                  <RotateCw size={20} />
                </button>
              </div>
            </div>
            <div className="bg-gray-900 flex items-center justify-center p-4 min-h-[500px]">
              <img
                src={photo.fileUrl}
                alt={photo.alt}
                className="max-w-full max-h-[500px] object-contain transition-transform duration-300"
                style={{ transform: `rotate(${rotation}deg)` }}
              />
            </div>

            {/* 圖片資訊 */}
            <div className="mt-4 space-y-2 text-sm text-gray-400">
              <div className="flex justify-between">
                <span>檔案大小：</span>
                <span>{photo.fileSize ? `${(photo.fileSize / 1024).toFixed(2)} KB` : "未知"}</span>
              </div>
              <div className="flex justify-between">
                <span>尺寸：</span>
                <span>{photo.width && photo.height ? `${photo.width} × ${photo.height}` : "未知"}</span>
              </div>
              <div className="flex justify-between">
                <span>上傳時間：</span>
                <span>{new Date(photo.createdAt).toLocaleString("zh-TW")}</span>
              </div>
              <div className="flex justify-between">
                <span>最後更新：</span>
                <span>{new Date(photo.updatedAt).toLocaleString("zh-TW")}</span>
              </div>
            </div>
          </div>

          {/* 右側：編輯表單 */}
          <div className="bg-gray-950 border border-gray-800 p-6">
            <h2 className="text-lg font-light tracking-wide mb-6">編輯資訊</h2>

            <div className="space-y-6">
              {/* 檔名（唯讀） */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">檔名</label>
                <input
                  type="text"
                  value={photo.fileName}
                  disabled
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-800 text-gray-500"
                />
              </div>

              {/* Alt 文字 */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  Alt 文字（SEO 重要）
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="text"
                  value={editForm.alt}
                  onChange={(e) => setEditForm({ ...editForm, alt: e.target.value })}
                  placeholder="例：信義店門口外觀照片"
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-800 text-gray-100 focus:border-gray-600 outline-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  用於 SEO 和無障礙閱讀，請描述圖片內容
                </p>
              </div>

              {/* 分類 */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">分類</label>
                <select
                  value={editForm.category}
                  onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-800 text-gray-100 focus:border-gray-600 outline-none"
                >
                  <option value="general">一般</option>
                  <option value="product">商品</option>
                  <option value="store">門市</option>
                  <option value="article">文章</option>
                  <option value="banner">橫幅</option>
                  <option value="team">團隊</option>
                </select>
              </div>

              {/* 標籤 */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">標籤（逗號分隔）</label>
                <input
                  type="text"
                  value={editForm.tags}
                  onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })}
                  placeholder="例：咖啡豆, 衣索比亞, 耶加雪菲"
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-800 text-gray-100 focus:border-gray-600 outline-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  用於搜尋和分類，多個標籤用逗號分隔
                </p>
              </div>

              {/* 顯示位置 */}
              <div>
                <label className="block text-sm text-gray-400 mb-3">顯示位置</label>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer hover:bg-gray-900 p-3 transition-colors">
                    <input
                      type="checkbox"
                      checked={editForm.displayLocations.includes('homeCarousel')}
                      onChange={(e) => {
                        const isChecked = e.target.checked
                        setEditForm(prev => ({
                          ...prev,
                          displayLocations: isChecked
                            ? [...prev.displayLocations, 'homeCarousel']
                            : prev.displayLocations.filter(loc => loc !== 'homeCarousel')
                        }))
                      }}
                      className="w-5 h-5"
                    />
                    <div>
                      <div className="text-gray-100">首頁輪播圖</div>
                      <div className="text-xs text-gray-500">勾選後此照片將顯示在首頁輪播區塊</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* 圖片 URL */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">圖片 URL</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={photo.fileUrl}
                    disabled
                    className="flex-1 px-4 py-3 bg-gray-900 border border-gray-800 text-gray-500 text-sm"
                  />
                  <button
                    onClick={handleCopyUrl}
                    className="px-4 py-3 bg-green-600 hover:bg-green-700 text-white transition-colors flex items-center gap-2"
                  >
                    {copiedUrl ? <Check size={18} /> : <Copy size={18} />}
                    {copiedUrl ? "已複製" : "複製"}
                  </button>
                </div>
              </div>

              {/* 操作按鈕 */}
              <div className="flex gap-4 pt-4">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 text-white transition-colors"
                >
                  {saving ? "儲存中..." : "儲存變更"}
                </button>
                <button
                  onClick={() => navigate("/admin/photos")}
                  className="px-6 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 transition-colors"
                >
                  取消
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PhotoEditPage
